import { useRef, useEffect, useCallback } from "react";
import { useQuery } from "react-query";
import { axiosAuth, axiosNoAuth } from "./axiosConfig";
import { getApiErrorInfo } from "./apiError";

// DRY helpers and shared config
const CONNECTOR_BASE = process.env.REACT_APP_CONNECTOR_ROUTE || "";

/**
 * @param {string} path
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
const getConnector = (path) => axiosNoAuth.get(`${CONNECTOR_BASE}${path}`);

/**
 * @param {string} path
 * @param {any} body
 * @param {any} [config] - Optional axios config (e.g., timeout)
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
const postConnector = (path, body, config) =>
  axiosNoAuth.post(`${CONNECTOR_BASE}${path}`, body, config);

/**
 * @param {string} path
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
const getServer = (path) => axiosAuth.get(path);

/**
 * @param {string} path
 * @param {any} body
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
const postServer = (path, body) => axiosAuth.post(path, body);

/**
 * Normalize axios errors into a common shape (see api/apiError.js)
 * @param {any} err
 */
const toErrorInfo = (err) => {
  const info = getApiErrorInfo(err, "Request failed");
  return { ...info, status: info.status || 500 };
};

/**
 * Safe, minimal logger for axios errors
 * @param {string} ctx
 * @param {any} err
 */
const logAxiosError = (ctx, err) => {
  const info = toErrorInfo(err);
  // eslint-disable-next-line no-console
  console.error(
    `[${ctx}] ${info.status}: ${info.message}` +
      (info.requestId ? ` (request ${info.requestId})` : ""),
  );
};

/**
 * Fetch a scan result by scan_id from the connector app.
 * Returns the raw axios response to preserve status codes (200/202).
 * @param {string} scanId
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const getScanResult = (scanId) => getConnector(`/scan-result/${scanId}`);

/**
 * Health of the connector app
 * @param {(data:any)=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useConnectorAppHealth = (successFunc, errorLogger) => {
  return useQuery(
    ["connectorAppHealth", CONNECTOR_BASE],
    async () => {
      try {
        const response = await getConnector("/health");
        // Normalize the response to always have a healthy field
        const data = response.data || {};
        return {
          healthy: data.status === "running" || response.status === 200,
          healthSupported: true,
          sseSupported: data.sseSupported !== false, // Assume SSE supported unless explicitly false
          cancelSupported: data.cancelSupported !== false, // Assume cancel supported unless explicitly false
          status: response.status,
          message: data.message || "Connector app is healthy",
          ...data,
        };
      } catch (err) {
        // Backwards compatibility: older connector app versions may not have /health (404)
        if (err.response?.status === 404) {
          return {
            healthy: true, // Assume operational so rest of flow can proceed
            healthSupported: false,
            sseSupported: false, // Legacy connectors don't support SSE
            cancelSupported: false, // Legacy connectors don't support cancel
            status: 404,
            message:
              "Legacy Connector App: health check not supported. Proceeding optimistically.",
          };
        }
        const info = toErrorInfo(err);
        return {
          healthy: false,
          healthSupported: true,
          sseSupported: false,
          cancelSupported: false,
          status: info.status || 503,
          message: info.message || "Connector app is not reachable",
        };
      }
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: successFunc,
      onError: errorLogger,
    },
  );
};

/**
 * Fetch connector devices.
 * @param {{healthy?:boolean,status?:number,healthSupported?:boolean}} healthData
 * @param {(data:any)=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useConnectorDevices = (healthData, successFunc, errorLogger) => {
  return useQuery(
    ["connectorDevices", CONNECTOR_BASE],
    async () => {
      try {
        const response = await getConnector("/scan-devices");
        // Extract devices from response (support both legacy and new shapes)
        const devices =
          response.data?.data?.devices || response.data?.devices || [];
        return {
          status: response.status,
          devices: devices.map((device) => device.serial),
          message:
            devices.length === 0
              ? "No devices found"
              : `Found ${devices.length} device(s)`,
        };
      } catch (err) {
        const info = toErrorInfo(err);
        // Log here minimally; also surfaces via onError
        logAxiosError("connectorDevices", err);
        return {
          status: info.status,
          devices: [],
          message: info.message || "Error scanning devices",
        };
      }
    },
    {
      // Enable if health reports healthy OR legacy (404 health unsupported) OR explicitly marked unsupported
      enabled:
        healthData?.healthy === true ||
        healthData?.status === 404 ||
        healthData?.healthSupported === false,
      refetchOnWindowFocus: false,
      onSuccess: successFunc,
      onError: errorLogger,
    },
  );
};
/**
 * Fetch server devices by serial IDs.
 * @param {string[]|undefined} deviceSerials
 * @param {(data:any[])=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useServerDevices = (deviceSerials, successFunc, errorLogger) => {
  const keySerials = Array.isArray(deviceSerials)
    ? [...deviceSerials].sort()
    : [];
  return useQuery(
    ["serverDevices", keySerials],
    async () => {
      if (!deviceSerials || deviceSerials.length === 0) {
        return [];
      }

      const ids = deviceSerials;
      const devices = [];

      await Promise.all(
        ids.map(async (id) => {
          try {
            const result = await getServer(`/devices/${id}`);
            devices.push(result.data);
          } catch (err) {
            logAxiosError(
              `serverDevices: could not retrieve device serial ${id}`,
              err,
            );
          }
        }),
      );
      return devices;
    },
    {
      enabled: Array.isArray(deviceSerials) && deviceSerials.length > 0,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
      onSuccess: successFunc,
      onError: errorLogger,
    },
  );
};
/**
 * Calibrate connector device - initiates the calibration scan and returns scan_id and steps.
 * Does NOT save to the server; that happens after SSE completion.
 * Errors are caught and returned in the response object with status >= 400.
 * @param {string} id
 */
export const useConnectorCalibrate = (id) => {
  return useQuery(
    ["connectorCalibrate", id],
    async () => {
      let response = {};
      try {
        let lastCalibrationScan = null;
        try {
          const lastCalibrationResponse = await getServer(
            `/devices/get-last-calibration-scan/${id}`,
          );
          lastCalibrationScan = lastCalibrationResponse.data || null;
        } catch (scanErr) {
          // No previous calibration scan is normal for a new device. Any
          // other failure (DB issues, permissions, network) shouldn't block
          // calibration either: log it and proceed without previous scan data.
          if (scanErr?.code !== "calibration_scan_not_found") {
            const info = toErrorInfo(scanErr);
            console.warn(
              `Failed to fetch last calibration scan for device ${id}:`,
              `${info.status} ${info.message}`,
              info.requestId ? `(request ${info.requestId})` : "",
              "- proceeding without previous scan data",
            );
          }
          lastCalibrationScan = null;
        }

        const calibrationResponse = await postConnector(
          "/calibrate-device",
          {
            deviceSerial: id,
            lastCalibrationScan: lastCalibrationScan,
            scanType: "trace", //needed for legacy support, new connector apps ignore this field
          },
          {
            timeout: 60000, // 60 second timeout for connector app connection
          },
        );

        const data =
          calibrationResponse.data.data || calibrationResponse.data || {};
        // Return scan tracking info; actual save happens after SSE completes
        response = {
          status: calibrationResponse.status,
          scan_id: data?.scan_id || data?.scanId,
          scanSteps: data?.scanSteps,
          calibrationData: data, // store for later save
        };
      } catch (err) {
        console.error("Error during device calibration:", err);
        const info = toErrorInfo(err);
        // Include error response data for legacy connectors that return error info in response body
        const errorData = err?.response?.data || {};
        response = {
          status: info.status || 400,
          message: info.message || "Error calibrating device.",
          calibrationData: {
            ...errorData,
          },
        };
      }
      return response;
    },
    {
      enabled: false,
    },
  );
};

/**
 * Save calibration scan data to the server. Called after SSE scan completion.
 * @param {string} deviceSerial
 * @param {any} calibrationData - The data object from connector calibration response
 * @param {(data:any)=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useSaveCalibrationScan = (
  deviceSerial,
  calibrationData,
  successFunc,
  errorLogger,
) => {
  return useQuery(
    ["saveCalibrationScan", deviceSerial],
    async () => {
      try {
        const saveResponse = await postServer(
          "/devices/save-calibration-scan",
          {
            ...calibrationData,
            ...(calibrationData?.deviceSerial === undefined
              ? { deviceSerial }
              : {}),
          },
        );
        return {
          status: saveResponse.status,
          message: "Calibration data saved successfully",
          data: saveResponse.data,
        };
      } catch (err) {
        const info = toErrorInfo(err);
        console.error("Error saving calibration scan:", info);
        throw err;
      }
    },
    {
      enabled: false,
      onSuccess: successFunc,
      onError: errorLogger,
    },
  );
};
/**
 * Calibrate server device - updates lastCalibration timestamp based on scan success status.
 * @param {string} id
 * @param {string} date
 * @param {boolean | () => boolean} isSuccess - Whether to update timestamp. Can be a boolean or function that returns one.
 * @param {(data:any)=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useServerCalibrate = (
  id,
  date,
  isSuccess,
  successFunc,
  errorLogger,
) => {
  return useQuery(
    ["serverCalibrate", id],
    async () => {
      // Determine if scan was successful - can be a boolean or function
      const scanSuccessful =
        typeof isSuccess === "function" ? isSuccess() : isSuccess;

      // Nothing to persist when the scan failed - skip the request rather
      // than sending an empty body, which the server rejects
      // (updateDeviceSchema requires at least one key) on every failed
      // calibration attempt.
      if (!scanSuccessful) {
        return { status: 204 };
      }

      try {
        const { status, data } = await axiosAuth.patch(`/devices/${id}`, {
          lastCalibration: date,
        });
        return { status, data };
      } catch (err) {
        const info = toErrorInfo(err);
        logAxiosError("serverCalibrate: patch calibration", err);
        return {
          status: info.status,
          message: info.message || "Error saving calibration date to server.",
          requestId: info.requestId,
        };
      }
    },
    {
      enabled: false,
      onSuccess: successFunc,
      onError: errorLogger,
    },
  );
};
/**
 * Get measurement trace from connector device.
 * @param {string} deviceSerial
 * @param {"trace"|"bulk"|string} scanType
 * @param {(err:any)=>void} [errorLogger]
 */
export const useGetMeasurementTrace = (deviceSerial, scanType, errorLogger) => {
  return useQuery(
    ["getMeasurementTrace", deviceSerial, scanType],
    async () => {
      try {
        const { data } = await postConnector("/get-trace-measurement", {
          deviceSerial,
          scanType,
        });
        return data;
      } catch (err) {
        logAxiosError("getMeasurementTrace", err);
        throw err;
      }
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    },
  );
};
/**
 * Get bulk measurement from connector device.
 * @param {string} deviceSerial
 * @param {"bulk"|"trace"|string} scanType
 * @param {(err:any)=>void} [errorLogger]
 */
export const useGetMeasurementBulk = (deviceSerial, scanType, errorLogger) => {
  return useQuery(
    ["getMeasurementBulk", deviceSerial, scanType],
    async () => {
      try {
        const { data } = await postConnector("/get-bulk-measurement", {
          deviceSerial,
          scanType,
        });
        return data;
      } catch (err) {
        logAxiosError("getMeasurementBulk", err);
        throw err;
      }
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    },
  );
};

/**
 * Cancel an in-progress scan on a device.
 * For legacy connectors that don't support cancel, this is a no-op but doesn't fail.
 *
 * @param {string} deviceSerial
 * @param {string} scanId
 * @returns {Promise<any>}
 */
export const cancelScan = async (deviceSerial, scanId) => {
  try {
    const { data } = await postConnector("/cancel-scan", {
      deviceSerial,
      scan_id: scanId,
    });
    return data;
  } catch (err) {
    // Legacy connectors may not support cancel endpoint (404)
    // Gracefully handle this by throwing a user-friendly error so callers can react appropriately
    if (err.response?.status === 404) {
      console.warn(
        "Legacy connector: cancel-scan endpoint not supported. Continuing without cancellation.",
      );
      const userError = new Error("Cancel not supported on legacy connector");
      userError.name = "CancelNotSupportedError";
      // Preserve original response for upstream error handling if needed
      userError.response = err.response;
      throw userError;
    }
    logAxiosError("cancelScan", err);
    throw err;
  }
};

/**
 * Hook to monitor scan progress via Server-Sent Events (SSE).
 *
 * @param {string} scanId - The ID of the scan to monitor
 * @param {(progress: any) => void} onProgress - Callback fired on progress updates. For optimal performance, memoize this callback with useCallback.
 * @param {(result: any) => void} onComplete - Callback fired when scan completes. For optimal performance, memoize this callback with useCallback.
 * @param {(error: any) => void} [onError] - Callback fired on error or timeout. For optimal performance, memoize this callback with useCallback.
 * @returns {() => void} Function to manually close the SSE connection
 */
export const useScanProgress = (scanId, onProgress, onComplete, onError) => {
  const eventSourceRef = useRef(null);
  const hasErroredRef = useRef(false);

  // Wrap callbacks to provide safe defaults without recreating on every parent render.
  // Callers should memoize their callbacks to avoid unnecessary EventSource recreation.
  const stableOnProgress = useCallback(onProgress || (() => {}), []);
  const stableOnComplete = useCallback(onComplete || (() => {}), []);
  const stableOnError = useCallback(onError || (() => {}), []);

  // Establish SSE subscription for scan progress updates
  useEffect(() => {
    if (!scanId) return;
    hasErroredRef.current = false;
    try {
      const sseBaseUrl = CONNECTOR_BASE || "http://localhost:9092";
      const eventSource = new EventSource(
        `${sseBaseUrl}/scan-status/${scanId}`,
      );
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.status === "complete") {
            stableOnComplete(data.result);
            eventSource.close();
          } else if (data.status === "in_progress") {
            stableOnProgress(data.progress);
            // data.progress contains: message, step_num, step_name, current_scan, total_scans, etc.
          } else if (data.status === "timeout") {
            console.error("Scan timed out");
            const error = new Error("Scan timed out");
            error.timeout = true;
            if (!hasErroredRef.current) {
              hasErroredRef.current = true;
              stableOnError(error);
            }
            eventSource.close();
          }
        } catch (parseErr) {
          console.error("Error parsing SSE message:", parseErr);
          if (!hasErroredRef.current) {
            hasErroredRef.current = true;
            stableOnError(parseErr);
          }
          eventSource.close();
        }
      };

      eventSource.onerror = (error) => {
        console.error("SSE Error:", error);
        if (!hasErroredRef.current) {
          hasErroredRef.current = true;
          stableOnError(error);
        }
        eventSource.close();
      };
    } catch (err) {
      console.error("Error creating EventSource:", err);
      if (!hasErroredRef.current) {
        hasErroredRef.current = true;
        stableOnError(err);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    }

    // Cleanup: close connection on unmount or if scanId changes
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [scanId, stableOnProgress, stableOnComplete, stableOnError]);

  // Return a function to manually close the connection
  return () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
  };
};
